(ns arthur.fx.http
  "The one place that talks to the server.

  Everything here returns a promise of a PARSED JS VALUE, not of CLJS data, and
  that is deliberate: a leaf is transit, and `domain/project` reads it straight out
  of the response object. A keywordising `js->clj` on the way past would turn the
  leaf path \"clip/c1/symbol/main/node/mouth\" into a keyword whose name is
  \"c1/symbol/main/node/mouth\",
  losing the prefix — a corruption that only shows up on the way back in.

  CSRF IS NOT EXEMPTED. The page renders `{% csrf_token %}`, so Django sets its
  cookie, and every unsafe request carries it back in the header Django looks for.
  Nine lines against `@csrf_exempt` on an API that writes the document."
  (:require [clojure.string :as str]))

(defn csrf-token []
  (some (fn [pair]
          (let [[k v] (str/split pair #"=" 2)]
            (when (= "csrftoken" (str/trim (or k ""))) v)))
        (str/split (or (.-cookie js/document) "") #";")))

(defn- failure
  "Turn a non-2xx into an ex-info carrying what the server said.

  The server's message is the useful one — \"this clip names tier-2 blocks the
  server does not have\" — and a status code alone would put the interesting half
  of it in a console nobody is watching.

  Built rather than thrown, because the two transports below raise it in
  different ways: `fetch` throws inside a `.then` and XHR has to reject a promise
  by hand."
  [status body]
  (ex-info (or (some-> body .-error)
               (str "the server answered " status))
           {:status status
            :body   (when body (js->clj body :keywordize-keys true))}))

(defn- parse
  "A response body as a parsed JS value, or nil. An empty 204 and a 500 whose
  body is an HTML error page both land here, and neither is worth an exception."
  [text]
  (when (seq text)
    (try (js/JSON.parse text) (catch :default _ nil))))

(defn request!
  ([method url] (request! method url nil))
  ([method url body]
   (let [form? (instance? js/FormData body)
         headers #js {"Accept" "application/json"}
         options #js {:method method :cache "no-store" :headers headers}]
    (when (and body (not form?))
      (aset headers "Content-Type" "application/json"))
    (when (not= "GET" method)
      (aset headers "X-CSRFToken" (or (csrf-token) "")))
    (when body
      (aset options "body" (if form? body (js/JSON.stringify body))))
    (-> (js/fetch url options)
       (.then (fn [response]
                (-> (.text response)
                    (.then (fn [text]
                             (let [parsed (parse text)]
                               (if (.-ok response)
                                 parsed
                                 (throw (failure (.-status response) parsed)))))))))))))

(defn POST-form
  "A multipart POST, optionally reporting how much of the body has gone out.

  XMLHttpRequest RATHER THAN `fetch`, for the one thing fetch cannot do: a
  Request built from a FormData gives no way to observe its own upload, so the
  promise settles when the response arrives and everything before that is a
  single unknown. On the one call that sends a whole video that unknown is
  minutes long, and a spinner with no number on it is most of what \"slow\" means
  to whoever is waiting — so this is worth the twenty lines XHR costs.

  `on-progress` is called with the fraction sent, 0.0 to 1.0. Only while the
  browser can say what the total is: a body whose length it cannot compute
  reports nothing rather than a made-up number.

  CONTENT-TYPE IS DELIBERATELY NOT SET. The browser has to write the multipart
  boundary into it, and setting it by hand sends a body the server cannot parse."
  ([url body] (request! "POST" url body))
  ([url body on-progress]
   (js/Promise.
    (fn [resolve reject]
      (let [xhr (js/XMLHttpRequest.)]
        (.open xhr "POST" url)
        (.setRequestHeader xhr "Accept" "application/json")
        (.setRequestHeader xhr "X-CSRFToken" (or (csrf-token) ""))
        (set! (.. xhr -upload -onprogress)
              (fn [^js event]
                (when (and (.-lengthComputable event) (pos? (.-total event)))
                  (on-progress (/ (.-loaded event) (.-total event))))))
        (set! (.-onload xhr)
              (fn []
                (let [parsed (parse (.-responseText xhr))]
                  (if (<= 200 (.-status xhr) 299)
                    (resolve parsed)
                    (reject (failure (.-status xhr) parsed))))))
        ;; The three ways a send produces no response at all. None of them has a
        ;; status, so none of them can go through `failure` — and a dropped
        ;; upload has to say so, because it is the one failure here that is
        ;; routine rather than a bug.
        (set! (.-onerror xhr)
              (fn [_] (reject (ex-info "the upload did not reach the server" {}))))
        (set! (.-onabort xhr)
              (fn [_] (reject (ex-info "the upload was cancelled" {}))))
        (set! (.-ontimeout xhr)
              (fn [_] (reject (ex-info "the upload timed out" {}))))
        (.send xhr body))))))

(defn GET [url] (request! "GET" url))
(defn POST [url body] (request! "POST" url body))
(defn PUT [url body] (request! "PUT" url body))
(defn PATCH [url body] (request! "PATCH" url body))
